import { z } from 'zod';
import type { AdminPageMeta } from './admin.js';
import { SportNameSchema, type SportName } from './profile.js';

/**
 * F29 — Teams.
 *
 * The single source of truth for the Team entity + roster + invitations +
 * join-requests + audit log type surface. Consumed by:
 *   - @bravemove/db schema CHECK constraints (verbatim text-enum lists)
 *   - apps/api services + routes
 *   - apps/portal + apps/mobile + apps/admin frontend clients
 *
 * See plans/29_F29_teams.md for the 18 locked decisions. The most load-bearing:
 *   - D1: single sport per team, sport_name IMMUTABLE after create
 *   - D2: captain + member only (2-value role enum)
 *   - D3: privacy + join_policy per team (captain picks at create)
 *   - D9: member-visible audit log
 *   - D11: admin suspend (reversible) + disband (terminal)
 *   - D14: name 2–80 chars, duplicates allowed, pg_trgm fuzzy search
 *   - D16: HMAC-SHA256 signed tokens, dedicated team_invitations table
 *   - D17: two-layer idempotency (direct=partial-unique; shareable=PK on redemption)
 *   - D18: NO If-Match concurrency at MVP (F36 adds when co-captains arrive)
 */

// ─── Length / range bounds (mirror schema CHECK constraints) ─────────────────

export const TEAM_NAME_MIN = 2;
export const TEAM_NAME_MAX = 80;
export const TEAM_DESCRIPTION_MAX = 280;
export const TEAM_SUSPENDED_REASON_MAX = 500;
export const TEAM_REMOVED_REASON_MAX = 200;
export const TEAM_INVITATION_WITHDRAWN_REASON_MAX = 200;
export const TEAM_INVITATION_EMAIL_MIN = 3;
export const TEAM_INVITATION_EMAIL_MAX = 254;
export const TEAM_INVITATION_PHONE_MIN = 4;
export const TEAM_INVITATION_PHONE_MAX = 32;
export const TEAM_JOIN_REQUEST_MESSAGE_MAX = 280;
export const TEAM_JOIN_REQUEST_DENIED_REASON_MAX = 200;

export const TEAM_INVITATION_DEFAULT_EXPIRY_DAYS = 7;
export const TEAM_INVITATION_MAX_EXPIRY_DAYS = 30;
export const TEAM_INVITATION_DEFAULT_MAX_USES = 50;
export const TEAM_INVITATION_MAX_MAX_USES = 500;

export const TEAM_AVERAGE_SKILL_RATING_MIN = 1.0;
export const TEAM_AVERAGE_SKILL_RATING_MAX = 7.0;

// ─── Enums (text-literal unions kept in sync with schema CHECKs) ─────────────

export const TEAM_MEMBER_ROLES = ['captain', 'member'] as const;
export type TeamMemberRole = (typeof TEAM_MEMBER_ROLES)[number];
export const TeamMemberRoleSchema = z.enum(TEAM_MEMBER_ROLES);

// `removed_by_admin` — F43-FC admin removal (migration 0049 widened the CHECK;
// FIX 5.26 brought the union in line).
export const TEAM_MEMBER_STATUSES = [
  'active',
  'member_left',
  'removed_by_captain',
  'removed_by_admin',
] as const;
export type TeamMemberStatus = (typeof TEAM_MEMBER_STATUSES)[number];
export const TeamMemberStatusSchema = z.enum(TEAM_MEMBER_STATUSES);

export const TEAM_PRIVACY_VALUES = ['public', 'invite_only'] as const;
export type TeamPrivacy = (typeof TEAM_PRIVACY_VALUES)[number];
export const TeamPrivacySchema = z.enum(TEAM_PRIVACY_VALUES);

export const TEAM_JOIN_POLICIES = ['direct_join', 'request_approval'] as const;
export type TeamJoinPolicy = (typeof TEAM_JOIN_POLICIES)[number];
export const TeamJoinPolicySchema = z.enum(TEAM_JOIN_POLICIES);

export const TEAM_STATUSES = ['active', 'suspended', 'disbanded'] as const;
export type TeamStatus = (typeof TEAM_STATUSES)[number];
export const TeamStatusSchema = z.enum(TEAM_STATUSES);

export const TEAM_INVITATION_KINDS = ['direct', 'shareable_link'] as const;
export type TeamInvitationKind = (typeof TEAM_INVITATION_KINDS)[number];
export const TeamInvitationKindSchema = z.enum(TEAM_INVITATION_KINDS);

/**
 * Derived invitation lifecycle status — NOT a stored column. Computed from
 * `(consumed_at, withdrawn_at, expires_at, consumed_count, max_uses)`:
 *   - 'pending'   — direct: consumed_at IS NULL AND withdrawn_at IS NULL AND expires_at > now()
 *                   shareable: consumed_count < max_uses AND withdrawn_at IS NULL AND expires_at > now()
 *   - 'consumed'  — direct: consumed_at IS NOT NULL
 *                   shareable: max_uses IS NOT NULL AND consumed_count >= max_uses
 *   - 'expired'   — expires_at <= now() AND no other terminal flag set
 *   - 'withdrawn' — withdrawn_at IS NOT NULL
 */
export const TEAM_INVITATION_STATUSES = ['pending', 'consumed', 'expired', 'withdrawn'] as const;
export type TeamInvitationStatus = (typeof TEAM_INVITATION_STATUSES)[number];

export const TEAM_JOIN_REQUEST_STATUSES = [
  'pending',
  'approved',
  'denied',
  'withdrawn',
  'expired',
] as const;
export type TeamJoinRequestStatus = (typeof TEAM_JOIN_REQUEST_STATUSES)[number];
export const TeamJoinRequestStatusSchema = z.enum(TEAM_JOIN_REQUEST_STATUSES);

export const TEAM_AUDIT_ACTOR_KINDS = ['captain', 'member', 'admin', 'system'] as const;
export type TeamAuditActorKind = (typeof TEAM_AUDIT_ACTOR_KINDS)[number];

export const TEAM_AUDIT_TARGET_KINDS = [
  'team_member',
  'team_invitation',
  'team_join_request',
  'team',
] as const;
export type TeamAuditTargetKind = (typeof TEAM_AUDIT_TARGET_KINDS)[number];

export const TEAM_AUDIT_SURFACES = ['portal', 'mobile', 'admin', 'api', 'system'] as const;
export type TeamAuditSurface = (typeof TEAM_AUDIT_SURFACES)[number];

/** Captured by `TeamAuditService.write` — the action string is open enum at the
 * DB level (plain `text`); these are the actions F29 actually emits. */
export const TEAM_AUDIT_ACTIONS = [
  'team.created',
  'team.updated',
  'team.privacy_changed',
  'team.join_policy_changed',
  'team.disbanded',
  'team.suspended',
  'team.reinstated',
  'team.disbanded_by_admin',
  'team.photo_uploaded',
  'invitation.issued',
  'invitation.withdrawn',
  'invitation.accepted',
  'invitation.declined',
  'member.removed',
  'member.left',
  'captaincy.transferred',
  'join_request.received',
  'join_request.approved',
  'join_request.denied',
  'join_request.withdrawn',
] as const;
export type TeamAuditAction = (typeof TEAM_AUDIT_ACTIONS)[number];

// ─── Error codes (20 — Section 17/3 in the F29 plan) ─────────────────────────

export const TEAM_ERROR_CODES = [
  'TEAM_NOT_FOUND',
  'TEAM_SUSPENDED',
  'TEAM_DISBANDED',
  'NOT_TEAM_MEMBER',
  'NOT_TEAM_CAPTAIN',
  'CAPTAIN_MUST_TRANSFER_OR_DISBAND',
  'INVITATION_NOT_FOUND',
  'INVITATION_EXPIRED',
  'INVITATION_WITHDRAWN',
  'INVITATION_EXHAUSTED',
  'INVITATION_ALREADY_CONSUMED',
  'INVALID_INVITATION_TOKEN',
  'JOIN_REQUEST_NOT_FOUND',
  'JOIN_REQUEST_ALREADY_OPEN',
  'JOIN_REQUEST_TEAM_NOT_PUBLIC',
  'TEAM_NAME_INVALID',
  'TEAM_PRIVACY_INVALID',
  'PROTECTED_FIELD',
  'STALE_TEAM',
  'ALREADY_MEMBER',
] as const;
export type TeamErrorCode = (typeof TEAM_ERROR_CODES)[number];

// ─── Photo variants (mirror F12 pattern; F29 has 2 photo kinds × 2 variants) ─

export type TeamAvatarVariants = {
  readonly thumb_url: string;
  readonly card_url: string;
};

export type TeamCoverVariants = {
  readonly card_url: string;
  readonly hero_url: string;
};

// ─── Raw DB row shapes (mirror packages/db/src/schema.ts) ────────────────────

export type Team = {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly sportName: SportName;
  readonly homeVenueId: string | null;
  readonly homeGovernorateIso: string | null;
  readonly homeCazaCode: string | null;
  readonly avatarUrl: string | null;
  readonly avatarVariants: TeamAvatarVariants | null;
  readonly coverUrl: string | null;
  readonly coverVariants: TeamCoverVariants | null;
  readonly privacy: TeamPrivacy;
  readonly joinPolicy: TeamJoinPolicy;
  readonly status: TeamStatus;
  /** F31 writes this; nullable until match outcomes accumulate. */
  readonly averageSkillRating: number | null;
  readonly memberCount: number;
  readonly countryCode: string;
  readonly captainLastActiveAt: string | null;
  readonly disbandedAt: string | null;
  readonly suspendedAt: string | null;
  readonly suspendedByAdminId: string | null;
  readonly suspendedReason: string | null;
  readonly createdByUserId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type TeamMember = {
  readonly id: string;
  readonly teamId: string;
  readonly userId: string;
  readonly role: TeamMemberRole;
  readonly status: TeamMemberStatus;
  readonly joinedAt: string;
  readonly leftAt: string | null;
  readonly removedAt: string | null;
  readonly removedByUserId: string | null;
  readonly removedReason: string | null;
  readonly invitedViaInvitationId: string | null;
  readonly joinedViaJoinRequestId: string | null;
  readonly countryCode: string;
};

export type TeamInvitation = {
  readonly id: string;
  readonly teamId: string;
  readonly kind: TeamInvitationKind;
  readonly inviteeUserId: string | null;
  readonly inviteeEmail: string | null;
  readonly inviteePhone: string | null;
  readonly issuedByUserId: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly maxUses: number | null;
  readonly consumedCount: number;
  readonly consumedAt: string | null;
  readonly withdrawnAt: string | null;
  readonly withdrawnReason: string | null;
  readonly countryCode: string;
  /** Derived from the row state at read time. */
  readonly derivedStatus: TeamInvitationStatus;
};

export type TeamInvitationRedemption = {
  readonly invitationId: string;
  readonly userId: string;
  readonly redeemedAt: string;
  readonly countryCode: string;
};

export type TeamJoinRequest = {
  readonly id: string;
  readonly teamId: string;
  readonly requesterUserId: string;
  readonly status: TeamJoinRequestStatus;
  readonly message: string | null;
  readonly requestedAt: string;
  readonly decidedAt: string | null;
  readonly decidedByUserId: string | null;
  readonly deniedReason: string | null;
  readonly countryCode: string;
};

export type TeamAuditEntry = {
  readonly id: string;
  readonly teamId: string;
  readonly actorUserId: string | null;
  readonly actorAdminId: string | null;
  readonly actorKind: TeamAuditActorKind;
  readonly action: TeamAuditAction | string; // open enum at DB level
  readonly targetKind: TeamAuditTargetKind | null;
  readonly targetId: string | null;
  readonly payload: Record<string, unknown>;
  readonly surface: TeamAuditSurface;
  readonly createdAt: string;
  readonly countryCode: string;
};

// ─── Viewer-aware read shapes ────────────────────────────────────────────────

/** Anonymous / signed-in-non-member projection. No roster details, no audit. */
export type TeamSummary = {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly sportName: SportName;
  readonly homeCazaCode: string | null;
  readonly homeGovernorateIso: string | null;
  readonly avatarCardUrl: string | null;
  readonly coverHeroUrl: string | null;
  readonly privacy: TeamPrivacy;
  readonly joinPolicy: TeamJoinPolicy;
  readonly memberCount: number;
  readonly averageSkillRating: number | null;
  readonly captainDisplayName: string | null;
  /** Signed-in non-member viewers only (`GET /me/teams/:id`): their open join request. */
  readonly myPendingJoinRequestId?: string | null;
};

/** Member or admin projection — full roster + invitations + join requests +
 * audit log (audit paginated separately). */
export type TeamDetail = {
  readonly team: Team;
  readonly captain: TeamMemberWithUser;
  readonly members: ReadonlyArray<TeamMemberWithUser>;
  /** Captain-only fields — undefined for non-captain member viewers. */
  readonly openInvitations?: ReadonlyArray<TeamInvitation>;
  readonly openJoinRequests?: ReadonlyArray<TeamJoinRequestWithRequester>;
  /** Recent audit entries (paginated separately via GET .../audit-log). */
  readonly recentAuditEntries: ReadonlyArray<TeamAuditEntry>;
};

/** TeamMember enriched with display name + avatar so the roster UI doesn't
 * have to round-trip per row. */
export type TeamMemberWithUser = TeamMember & {
  readonly displayName: string;
  readonly avatarUrl: string | null;
  /** Display-only; F09 v2 renders the tick from `verified`, never from this (E32). */
  readonly verifiedAt: string | null;
  /** F09 v2 — the member's PLAYER tick. */
  readonly verified: boolean;
};

export type TeamJoinRequestWithRequester = TeamJoinRequest & {
  readonly requesterDisplayName: string;
  readonly requesterAvatarUrl: string | null;
};

/** Public search result row — `TeamSummary` + computed fields the search UI
 * needs (e.g. distance from viewer, available-spot-count). F32 may extend. */
export type TeamSearchResultRow = TeamSummary;

// ─── Pagination cursor + envelope (matches F29 plan §7 + project convention) ─

export type PaginatedTeams = {
  readonly data: ReadonlyArray<TeamSearchResultRow>;
  readonly nextCursor: string | null;
  /** Present when page-numbered pagination is used (admin table). */
  readonly pagination?: AdminPageMeta;
};

// ─── Zod input schemas ───────────────────────────────────────────────────────

const teamNameField = z.string().min(TEAM_NAME_MIN).max(TEAM_NAME_MAX);
const teamDescriptionField = z.string().max(TEAM_DESCRIPTION_MAX).nullable();
const teamReasonField = z.string().min(1).max(TEAM_REMOVED_REASON_MAX);

export const CreateTeamInputSchema = z
  .object({
    name: teamNameField,
    description: teamDescriptionField.optional(),
    sportName: SportNameSchema,
    privacy: TeamPrivacySchema,
    joinPolicy: TeamJoinPolicySchema,
    homeVenueId: z.string().uuid().nullable().optional(),
  })
  .strict();
export type CreateTeamInput = z.infer<typeof CreateTeamInputSchema>;

/**
 * Captain-only edit. sport_name deliberately ABSENT — D1 immutability. Privacy +
 * join_policy may freely change; the server logs a `team.privacy_changed` +
 * `team.join_policy_changed` audit row.
 */
export const UpdateTeamInputSchema = z
  .object({
    name: teamNameField.optional(),
    description: teamDescriptionField.optional(),
    privacy: TeamPrivacySchema.optional(),
    joinPolicy: TeamJoinPolicySchema.optional(),
    homeVenueId: z.string().uuid().nullable().optional(),
  })
  .strict();
export type UpdateTeamInput = z.infer<typeof UpdateTeamInputSchema>;

/**
 * Photo upload finalize input. Two photo kinds; the underlying Sharp variant
 * functions differ per kind (see apps/api/src/services/sharp-pipeline.ts —
 * generateTeamAvatarVariants / generateTeamCoverVariants).
 */
export const TEAM_PHOTO_KINDS = ['avatar', 'cover'] as const;
export type TeamPhotoKind = (typeof TEAM_PHOTO_KINDS)[number];
export const TeamPhotoKindSchema = z.enum(TEAM_PHOTO_KINDS);

export const RequestTeamPhotoUploadInputSchema = z
  .object({
    photoKind: TeamPhotoKindSchema,
    mime: z.enum(['image/jpeg', 'image/png', 'image/heic', 'image/webp']),
    sizeBytes: z
      .number()
      .int()
      .min(1)
      .max(5 * 1024 * 1024),
  })
  .strict();
export type RequestTeamPhotoUploadInput = z.infer<typeof RequestTeamPhotoUploadInputSchema>;

export const FinalizeTeamPhotoInputSchema = z
  .object({
    photoKind: TeamPhotoKindSchema,
    s3Key: z.string().min(1).max(512),
  })
  .strict();
export type FinalizeTeamPhotoInput = z.infer<typeof FinalizeTeamPhotoInputSchema>;

// ─── Invitations (discriminated union by kind) ───────────────────────────────

/**
 * Direct invite — captain picks ONE of three recipient channels.
 *
 * The "exactly one of (inviteeUserId | inviteeEmail | inviteePhone)" invariant
 * is enforced by `.superRefine` on the OUTER discriminatedUnion below (not
 * here as `.refine()`) because Zod's `discriminatedUnion` requires plain
 * ZodObject branches — a refined branch produces ZodEffects which the union
 * constructor rejects.
 */
export const IssueDirectTeamInvitationInputSchema = z
  .object({
    kind: z.literal('direct'),
    inviteeUserId: z.string().uuid().optional(),
    inviteeEmail: z
      .string()
      .email()
      .min(TEAM_INVITATION_EMAIL_MIN)
      .max(TEAM_INVITATION_EMAIL_MAX)
      .optional(),
    inviteePhone: z
      .string()
      .min(TEAM_INVITATION_PHONE_MIN)
      .max(TEAM_INVITATION_PHONE_MAX)
      .optional(),
    expiresInDays: z
      .number()
      .int()
      .min(1)
      .max(TEAM_INVITATION_MAX_EXPIRY_DAYS)
      .default(TEAM_INVITATION_DEFAULT_EXPIRY_DAYS)
      .optional(),
  })
  .strict();
export type IssueDirectTeamInvitationInput = z.infer<typeof IssueDirectTeamInvitationInputSchema>;

/** Shareable-link invite — no addressed recipient. */
export const IssueShareableLinkTeamInvitationInputSchema = z
  .object({
    kind: z.literal('shareable_link'),
    expiresInDays: z
      .number()
      .int()
      .min(1)
      .max(TEAM_INVITATION_MAX_EXPIRY_DAYS)
      .default(TEAM_INVITATION_DEFAULT_EXPIRY_DAYS)
      .optional(),
    maxUses: z
      .number()
      .int()
      .min(1)
      .max(TEAM_INVITATION_MAX_MAX_USES)
      .nullable()
      .default(TEAM_INVITATION_DEFAULT_MAX_USES)
      .optional(),
  })
  .strict();
export type IssueShareableLinkTeamInvitationInput = z.infer<
  typeof IssueShareableLinkTeamInvitationInputSchema
>;

export const IssueTeamInvitationInputSchema = z
  .discriminatedUnion('kind', [
    IssueDirectTeamInvitationInputSchema,
    IssueShareableLinkTeamInvitationInputSchema,
  ])
  .superRefine((v, ctx) => {
    if (v.kind === 'direct') {
      const count = (v.inviteeUserId ? 1 : 0) + (v.inviteeEmail ? 1 : 0) + (v.inviteePhone ? 1 : 0);
      if (count !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'direct invitation requires exactly one of inviteeUserId | inviteeEmail | inviteePhone',
          path: ['kind'],
        });
      }
    }
    if (v.kind === 'shareable_link') {
      // strict() already rejects extra fields; defence-in-depth assertion is
      // unnecessary here, but a future widening of the shareable_link shape
      // should keep the discriminator clean.
    }
  });
export type IssueTeamInvitationInput = z.infer<typeof IssueTeamInvitationInputSchema>;

export const AcceptTeamInvitationInputSchema = z
  .object({
    token: z.string().min(1).max(4096),
  })
  .strict();
export type AcceptTeamInvitationInput = z.infer<typeof AcceptTeamInvitationInputSchema>;

export const DeclineTeamInvitationInputSchema = AcceptTeamInvitationInputSchema;
export type DeclineTeamInvitationInput = z.infer<typeof DeclineTeamInvitationInputSchema>;

export const WithdrawTeamInvitationInputSchema = z
  .object({
    reason: z.string().min(1).max(TEAM_INVITATION_WITHDRAWN_REASON_MAX).optional(),
  })
  .strict();
export type WithdrawTeamInvitationInput = z.infer<typeof WithdrawTeamInvitationInputSchema>;

// ─── Join requests ───────────────────────────────────────────────────────────

export const CreateTeamJoinRequestInputSchema = z
  .object({
    message: z.string().min(1).max(TEAM_JOIN_REQUEST_MESSAGE_MAX).nullable().optional(),
  })
  .strict();
export type CreateTeamJoinRequestInput = z.infer<typeof CreateTeamJoinRequestInputSchema>;

export const RespondToTeamJoinRequestInputSchema = z
  .object({
    deniedReason: z.string().min(1).max(TEAM_JOIN_REQUEST_DENIED_REASON_MAX).optional(),
  })
  .strict();
export type RespondToTeamJoinRequestInput = z.infer<typeof RespondToTeamJoinRequestInputSchema>;

// ─── Membership mutations ────────────────────────────────────────────────────

export const RemoveTeamMemberInputSchema = z
  .object({
    reason: teamReasonField.optional(),
  })
  .strict();
export type RemoveTeamMemberInput = z.infer<typeof RemoveTeamMemberInputSchema>;

export const TransferTeamCaptaincyInputSchema = z
  .object({
    newCaptainUserId: z.string().uuid(),
  })
  .strict();
export type TransferTeamCaptaincyInput = z.infer<typeof TransferTeamCaptaincyInputSchema>;

// ─── Admin moderation ────────────────────────────────────────────────────────

export const SuspendTeamInputSchema = z
  .object({
    reason: z.string().min(1).max(TEAM_SUSPENDED_REASON_MAX),
  })
  .strict();
export type SuspendTeamInput = z.infer<typeof SuspendTeamInputSchema>;

export const ReinstateTeamInputSchema = z
  .object({
    reason: z.string().min(1).max(TEAM_SUSPENDED_REASON_MAX).optional(),
  })
  .strict();
export type ReinstateTeamInput = z.infer<typeof ReinstateTeamInputSchema>;

export const DisbandTeamByAdminInputSchema = SuspendTeamInputSchema;
export type DisbandTeamByAdminInput = z.infer<typeof DisbandTeamByAdminInputSchema>;

// ─── Search + pagination ─────────────────────────────────────────────────────

export const TeamSearchInputSchema = z
  .object({
    q: z.string().min(1).max(80).optional(),
    sportName: SportNameSchema.optional(),
    governorateIso: z.string().min(1).max(16).optional(),
    cazaCode: z.string().min(1).max(64).optional(),
    cursor: z.string().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20).optional(),
  })
  .strict();
export type TeamSearchInput = z.infer<typeof TeamSearchInputSchema>;
